import { Fragment, memo, useEffect, useMemo, useRef } from 'react';
import {
  formatMessageTime,
  groupMessagesByDay,
  isChatMessageDisplayable,
  sortMessagesByDate,
  type ChatMessage,
  type ProactiveGreetingPhrase,
} from '@teo/widget-shared';
import {
  diffMessageLists,
  formatMessageListDiff,
  getProactiveGreetingCache,
  getWidgetSession,
  isPopupDebugEnabled,
  isTypingPlaceholderMessage,
  isSyncDebugEnabled,
  logSync,
  readLauncherAckGreetingPhrases,
  resolveMessageReactKey,
  summarizeChatMessage,
  useWidgetModulesOptional,
} from '@teo/widget-core';
import { useWidget } from '../context/WidgetContext.js';
import { getMessageBubbleLayout } from '../rich-media/bubble-layout.js';
import { RichMessageContent } from '../rich-media';
import { OutgoingImageBubble } from '../rich-media/OutgoingImageBubble.js';
import { Avatar } from './Avatar.js';
import { DateSeparator } from './DateSeparator.js';
import { GreetingSeparator } from './GreetingSeparator.js';
import { MessageStatusIcon } from './MessageStatusIcon.js';
import { MessageText } from './MessageText.js';
import { IconRetry } from './icons.js';

export type DemoMessage = ChatMessage;

type MessageBubbleProps = {
  message: ChatMessage;
  onRetry?: (clientId: string) => void;
  /** Hide avatar when stacked under the same sender */
  stacked?: boolean;
  onMediaLoad?: () => void;
};

export const MessageBubble = memo(function MessageBubble({
  message,
  onRetry,
  stacked = false,
  onMediaLoad,
}: MessageBubbleProps) {
  const { locale, t } = useWidget();
  const modules = useWidgetModulesOptional();
  const richMediaEnabled = modules?.isEnabled('richMedia') ?? true;
  const isUser = message.role === 'user';
  const isManager = message.role === 'manager';
  const timeLabel = formatMessageTime(message.createdAt, locale);
  const messageKey = message.clientId ?? message.id;

  const legacyMedia =
    !richMediaEnabled &&
    message.contentType === 'image' &&
    message.mediaUrl;

  const userImageUrl =
    isUser &&
    message.contentType === 'image' &&
    (message.mediaUrl ?? message.attachment?.url);

  const useRichContent = richMediaEnabled && !userImageUrl;
  const bubbleLayout = useRichContent ? getMessageBubbleLayout(message) : null;

  const rowClass = [
    'teo-row',
    isUser ? 'teo-row--user' : 'teo-row--bot',
    isManager ? 'teo-row--manager' : '',
    !isUser && stacked ? 'teo-row--stacked' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rowClass}>
      {!isUser && !stacked && (
        <Avatar size="sm" />
      )}
      <div
        className={
          userImageUrl
            ? 'teo-bubble teo-bubble--media teo-bubble--outgoing-media'
            : bubbleLayout?.bubbleClass ?? 'teo-bubble'
        }
      >
        {userImageUrl ? (
          <OutgoingImageBubble
            message={message}
            timeLabel={timeLabel}
            messageKey={messageKey}
            onRetry={onRetry}
            onMediaLoad={onMediaLoad}
          />
        ) : useRichContent ? (
          <RichMessageContent message={message} onMediaLoad={onMediaLoad} />
        ) : legacyMedia ? (
          <img
            className="teo-bubble__image"
            src={message.mediaUrl}
            alt=""
            loading="lazy"
          />
        ) : (
          <MessageText text={message.text} />
        )}
        {!userImageUrl && (
        <div className="teo-bubble__meta">
          <time dateTime={message.createdAt}>{timeLabel}</time>
          {isUser && message.status && (
            <MessageStatusIcon
              status={message.status}
              sendError={message.sendError}
            />
          )}
          {isUser && message.status === 'failed' && onRetry && (
            <button
              type="button"
              className="teo-retry-btn"
              onClick={() => onRetry(messageKey)}
              aria-label={t.retrySend}
            >
              <IconRetry />
            </button>
          )}
        </div>
        )}
      </div>
    </div>
  );
}, messageBubblePropsEqual);

function messageBubblePropsEqual(
  prev: MessageBubbleProps,
  next: MessageBubbleProps,
): boolean {
  return (
    prev.message === next.message &&
    prev.stacked === next.stacked &&
    prev.onRetry === next.onRetry &&
    prev.onMediaLoad === next.onMediaLoad
  );
}

type MessageListProps = {
  messages: ChatMessage[];
  /** Greeting phrases from `POST /greeting` — one bot bubble each (first dialog, top). */
  welcomeMessages?: ProactiveGreetingPhrase[];
  /**
   * Shown offer after popup / mid-thread — merged into the timeline by
   * `pendingContextBaseTimeMs` so the user’s reply stays below greetings.
   */
  pendingContextMessages?: ProactiveGreetingPhrase[];
  /** Epoch ms when the offer was shown (phrase N → +N seconds). */
  pendingContextBaseTimeMs?: number;
  /** Current-page greeting phrases (cache) for mid-thread separator after inject. */
  knownGreetingPhrases?: ProactiveGreetingPhrase[];
  /** Legacy single welcome bubble (Storybook / settings fallback). */
  welcomeText?: string;
  historyLoading?: boolean;
  historyError?: boolean;
  onHistoryRetry?: () => void;
  onRetry?: (clientId: string) => void;
  onMediaLoad?: () => void;
  /** When true, parent provides scroll container */
  embedded?: boolean;
  popupDebug?: boolean;
};

const PENDING_GREETING_CLIENT_PREFIX = 'pending-greeting-';
const GREETING_PHRASE_INTERVAL_MS = 1_000;

type MessageDayGroup = ReturnType<typeof groupMessagesByDay>[number];

/**
 * A new dialog has no server messages yet, but its local greeting still belongs
 * to today's timeline. Keeping the date group from the first render prevents
 * the separator from jumping above the greeting after the first backend sync.
 */
export function ensureGreetingDateGroup(
  groups: MessageDayGroup[],
  showLocalGreeting: boolean,
  now = new Date(),
): MessageDayGroup[] {
  if (!showLocalGreeting || groups.length > 0) return groups;
  return [{ dateKey: now.toISOString().slice(0, 10), messages: [] }];
}

function normalizePhraseText(text: string): string {
  return text
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/[—–−‐‑]/g, '-');
}

/** Ephemeral mid-thread greeting rows merged into the timeline before backend inject. */
export function isPendingGreetingMessage(message: ChatMessage): boolean {
  return String(message.clientId ?? message.id).startsWith(
    PENDING_GREETING_CLIENT_PREFIX,
  );
}

/** Phrase texts that identify a greeting bubble (pending, ack, or current cache). */
export function collectGreetingPhraseTexts(options: {
  contextPhrases?: ProactiveGreetingPhrase[];
  welcomePhrases?: ProactiveGreetingPhrase[];
  knownPhrases?: ProactiveGreetingPhrase[];
  pathname?: string;
}): Set<string> {
  const texts = new Set<string>();
  for (const phrase of [
    ...(options.contextPhrases ?? []),
    ...(options.welcomePhrases ?? []),
    ...(options.knownPhrases ?? []),
  ]) {
    if (phrase.type === 'text' && phrase.payload.trim()) {
      texts.add(normalizePhraseText(phrase.payload));
    }
  }

  const session = getWidgetSession();
  if (!session?.slug) return texts;

  for (const phrase of readLauncherAckGreetingPhrases(session.slug)) {
    texts.add(normalizePhraseText(phrase));
  }

  const pathname =
    options.pathname ??
    (typeof window !== 'undefined' ? window.location.pathname : '/');
  const entry = getProactiveGreetingCache(session.slug, pathname);
  for (const phrase of entry?.greetingPhrases ?? []) {
    if (phrase.type === 'text' && phrase.payload.trim()) {
      texts.add(normalizePhraseText(phrase.payload));
    }
  }

  return texts;
}

export function isGreetingTimelineMessage(
  message: ChatMessage,
  greetingTexts: ReadonlySet<string>,
): boolean {
  if (isPendingGreetingMessage(message)) return true;
  if (message.role !== 'bot' && message.role !== 'manager') return false;
  const text = normalizePhraseText(message.text ?? '');
  return text.length > 0 && greetingTexts.has(text);
}

/**
 * True when bot/manager rows already contain the greeting phrase sequence
 * (anywhere in the thread). Stage may inject `history` after the AI reply, so
 * trailing-only match would leave ephemeral pending bubbles and cause duplicates.
 */
export function messagesContainPhraseSequence(
  messages: ChatMessage[],
  phrases: ProactiveGreetingPhrase[],
): boolean {
  const payloads = phrases
    .filter((p) => p.type === 'text' && p.payload.trim())
    .map((p) => normalizePhraseText(p.payload));
  if (payloads.length === 0) return false;

  const bots = messages.filter(
    (m) =>
      (m.role === 'bot' || m.role === 'manager') &&
      Boolean(m.text?.trim()) &&
      !isTypingPlaceholderMessage(m) &&
      !String(m.clientId ?? m.id).startsWith(PENDING_GREETING_CLIENT_PREFIX),
  );
  if (bots.length < payloads.length) return false;

  for (let start = 0; start <= bots.length - payloads.length; start += 1) {
    const match = payloads.every(
      (payload, index) =>
        normalizePhraseText(bots[start + index]?.text ?? '') === payload,
    );
    if (match) return true;
  }
  return false;
}

/** @deprecated Prefer messagesContainPhraseSequence — same check, anywhere in thread. */
export function trailingMessagesMatchPhrases(
  messages: ChatMessage[],
  phrases: ProactiveGreetingPhrase[],
): boolean {
  return messagesContainPhraseSequence(messages, phrases);
}

export function pendingContextPhrasesToMessages(
  phrases: ProactiveGreetingPhrase[],
  baseTimeMs: number,
): ChatMessage[] {
  return phrases
    .filter((item) => item.type === 'text' && Boolean(item.payload?.trim()))
    .map((phrase, index) => {
      const clientId = `${PENDING_GREETING_CLIENT_PREFIX}${index}`;
      return {
        id: clientId,
        clientId,
        role: 'bot' as const,
        text: phrase.payload.trim(),
        createdAt: new Date(
          baseTimeMs + index * GREETING_PHRASE_INTERVAL_MS,
        ).toISOString(),
        status: 'sent' as const,
        contentType: 'text' as const,
      };
    });
}

export function MessageList({
  messages,
  welcomeMessages,
  pendingContextMessages,
  pendingContextBaseTimeMs,
  knownGreetingPhrases,
  welcomeText,
  historyLoading = false,
  historyError = false,
  onHistoryRetry,
  onRetry,
  onMediaLoad,
  embedded = false,
  popupDebug = false,
}: MessageListProps) {
  const { t } = useWidget();
  const visibleMessages = useMemo(
    () =>
      sortMessagesByDate(
        messages.filter(
          (message) =>
            !isTypingPlaceholderMessage(message) &&
            isChatMessageDisplayable(message),
        ),
      ),
    [messages],
  );
  const greetingPhrases = useMemo(
    () =>
      (welcomeMessages ?? []).filter(
        (item) => item.type === 'text' && Boolean(item.payload?.trim()),
      ),
    [welcomeMessages],
  );
  const contextPhrases = useMemo(
    () =>
      (pendingContextMessages ?? []).filter(
        (item) => item.type === 'text' && Boolean(item.payload?.trim()),
      ),
    [pendingContextMessages],
  );
  /** Hide local greeting only after assistant rows exist in the thread (post-refetch). */
  const hasAssistantMessagesInThread = useMemo(
    () =>
      visibleMessages.some(
        (message) => message.role === 'bot' || message.role === 'manager',
      ),
    [visibleMessages],
  );
  const showGreetingPhrases =
    greetingPhrases.length > 0 &&
    !historyError &&
    !hasAssistantMessagesInThread;
  const showWelcomeLegacy =
    !showGreetingPhrases &&
    Boolean(welcomeText?.trim()) &&
    !historyError &&
    !hasAssistantMessagesInThread;
  const showPendingContext =
    contextPhrases.length > 0 &&
    !historyError &&
    !messagesContainPhraseSequence(visibleMessages, contextPhrases);

  useEffect(() => {
    if (!popupDebug && !isPopupDebugEnabled()) return;
    if (contextPhrases.length === 0 && !showPendingContext) return;
    console.info('[Teo GreetingOffer]', 'MessageList pending', {
      contextPhrases: contextPhrases.length,
      showPendingContext,
      historyError,
      visibleCount: visibleMessages.length,
      baseTimeMs: pendingContextBaseTimeMs,
      alreadyInThread: messagesContainPhraseSequence(
        visibleMessages,
        contextPhrases,
      ),
      previews: contextPhrases.map((p) => p.payload.slice(0, 50)),
    });
  }, [
    contextPhrases,
    historyError,
    pendingContextBaseTimeMs,
    popupDebug,
    showPendingContext,
    visibleMessages,
  ]);

  const timelineMessages = useMemo(() => {
    if (!showPendingContext) return visibleMessages;
    const baseTimeMs =
      pendingContextBaseTimeMs != null &&
      Number.isFinite(pendingContextBaseTimeMs)
        ? pendingContextBaseTimeMs
        : Date.now() - contextPhrases.length * GREETING_PHRASE_INTERVAL_MS;
    return sortMessagesByDate([
      ...visibleMessages,
      ...pendingContextPhrasesToMessages(contextPhrases, baseTimeMs),
    ]);
  }, [
    contextPhrases,
    pendingContextBaseTimeMs,
    showPendingContext,
    visibleMessages,
  ]);

  const showHistoryLoading =
    historyLoading &&
    visibleMessages.length === 0 &&
    greetingPhrases.length === 0 &&
    contextPhrases.length === 0 &&
    !welcomeText?.trim();
  const groups = useMemo(
    () => groupMessagesByDay(timelineMessages),
    [timelineMessages],
  );
  const showLocalGreeting = showGreetingPhrases || showWelcomeLegacy;
  const renderedGroups = useMemo(
    () => ensureGreetingDateGroup(groups, showLocalGreeting),
    [groups, showLocalGreeting],
  );

  const greetingTexts = useMemo(
    () =>
      collectGreetingPhraseTexts({
        contextPhrases,
        welcomePhrases: greetingPhrases,
        knownPhrases: knownGreetingPhrases,
      }),
    [contextPhrases, greetingPhrases, knownGreetingPhrases, timelineMessages],
  );

  const prevMessagesRef = useRef<ChatMessage[]>(messages);
  useEffect(() => {
    if (!isSyncDebugEnabled()) return;
    const prev = prevMessagesRef.current;
    if (prev === messages) return;
    const diff = diffMessageLists(prev, messages);
    logSync('ui', `MessageList render — ${formatMessageListDiff(diff)}`, {
      ...diff,
      visible: visibleMessages.map(summarizeChatMessage),
    });
    prevMessagesRef.current = messages;
  }, [messages, visibleMessages]);

  const content = (
    <>
      {showHistoryLoading && (
        <div className="teo-history-state" aria-busy="true">
          <span className="teo-history-state__spinner" aria-hidden />
          <span>{t.historyLoading}</span>
        </div>
      )}
      {historyError && visibleMessages.length === 0 && (
        <div className="teo-history-state teo-history-state--error" role="alert">
          <span>{t.historyError}</span>
          {onHistoryRetry && (
            <button
              type="button"
              className="teo-history-state__retry"
              onClick={onHistoryRetry}
            >
              {t.historyRetry}
            </button>
          )}
        </div>
      )}
      {(() => {
        let previousInTimeline: ChatMessage | undefined;
        return renderedGroups.map((group, groupIndex) => (
          <Fragment key={group.dateKey}>
            <DateSeparator dateKey={group.dateKey} />
            {groupIndex === 0 &&
              showGreetingPhrases &&
              greetingPhrases.map((phrase, index) => (
                <div
                  key={`welcome-${index}`}
                  className={`teo-row teo-row--bot${index > 0 ? ' teo-row--stacked' : ''}`}
                >
                  {index === 0 && <Avatar size="sm" />}
                  <div className="teo-bubble">
                    <MessageText text={phrase.payload} />
                  </div>
                </div>
              ))}
            {groupIndex === 0 && showWelcomeLegacy && (
              <div className="teo-row teo-row--bot">
                <Avatar size="sm" />
                <div className="teo-bubble">
                  <MessageText text={welcomeText ?? ''} />
                </div>
              </div>
            )}
            {group.messages.map((msg, index) => {
              const prev =
                index > 0 ? group.messages[index - 1] : previousInTimeline;
              const isGreeting = isGreetingTimelineMessage(msg, greetingTexts);
              const prevIsGreeting = prev
                ? isGreetingTimelineMessage(prev, greetingTexts)
                : false;
              // Pending local greets + backend-injected greeting phrases after history.
              const showGreetingBreak =
                isGreeting && !prevIsGreeting && prev != null;
              const stacked =
                msg.role !== 'user' &&
                prev?.role === msg.role &&
                isGreeting === prevIsGreeting;

              if (index === group.messages.length - 1) {
                previousInTimeline = msg;
              }

              return (
                <Fragment key={resolveMessageReactKey(msg)}>
                  {showGreetingBreak ? <GreetingSeparator /> : null}
                  <MessageBubble
                    message={msg}
                    onRetry={onRetry}
                    onMediaLoad={onMediaLoad}
                    stacked={stacked}
                  />
                </Fragment>
              );
            })}
          </Fragment>
        ));
      })()}
    </>
  );

  if (embedded) {
    return <>{content}</>;
  }

  return <div className="teo-messages">{content}</div>;
}
